import {ViewType} from "../../../../gd/const/views/view-type"
import {SystemStatusGroup} from "../../../../gd/const/system-status"
import {ItemsHighlightMode, TasksViewAppSettings} from "../@types"
import {ColumnSettingsConfig} from "../../../manager/columns/types"
import TasksView_LocalSettings from "./local/local-settings"
import {AppSettingsMode, DEFAULT_TEMPLATE_ID} from "../../../../manager/app-settings/app-settings"
import {BUS_SETTINGS_MANAGER_CONFIG_CHANGED} from "../../../../gd/bus/bus-event-type"
import tasksView_Settings_onChangeParameter from "./functions/on-change-parameter"
import tasksView_Settings_onTemplateChanged from "./functions/on-template-changed"
import {SettingsVersion} from "../../../common/settings-manager"
import tasksView_Settings_isConfigChanged from "./functions/is-config-changed"
import tasksView_Settings_getActualConfig from "./functions/get-actual-config"
import {CardViewLayout} from "../../../../gd-ui/card-view-selector/card-view-selector"
import {BackGround} from "../../../../gd-app/panels/preferences-panel/settings-background/settings-background"

export type TasksView_Settings_Params =  {
    shouldInheritAppSettings    : boolean
    projectId?                  : ID
}


export type TitleColumnSettings = {
    maxWidth    : number | string
    minWidth    : number | string
}

// const debug = "TasksView_Settings"
const debug = false

export default class TasksView_Settings {

    public version                      : SettingsVersion = SettingsVersion.VERSION_3   // settings version for conversion of old settings into new one

    // In TypeScript, child properties are initialized only after the parent constructor (super) runs.
    // Therefore, `this.initProperties` should be invoked in the child class constructor,
    // and `actualSettings` must be passed as an argument to ensure properties are set up correctly.
    protected actualSettings            : TasksViewAppSettings | undefined

    public projectId?                   : ID = undefined  // set in project based views also used as current project for inheritance

    // save initial config for changes comparison in isConfigChanged() and to restore values on reset()
    // should be set to some default config in child class if appSettingsConfig is undefined
    public _storedAppSettingsConfig     : TasksViewAppSettings                          // settings which belongs to this view i.e not local/tpl/etc... but custom view or project view
    public localSettings                : TasksView_LocalSettings                       // local settings we use with current view
    public defaultAppSettings           : TasksViewAppSettings

    public _templateId?                 : string                                        // view settings preset template name if set -> use template config

    public _mode                        : AppSettingsMode = AppSettingsMode.CUSTOM
    public _inheritedFromProject        : any                                           // project we inherited from

    public shouldInheritAppSettings     : boolean = false                               // determines should app settings be inherited or not
    public templateIgnoredParams        : string[] = []                                 // params that are not in view template and stored separately (e.g. 'defaultZoom' for Gantt)

    public autoSaveFunction?            : func                                          // function to save settings according to the view (should be assigned a function that handles the save logic)

    // tasks view parameters in settings
    protected _columns!                 : ColumnSettingsConfig[]
    protected _systemStatusFilter       : SystemStatusGroup = SystemStatusGroup.OPEN    // used is task table to show hide archived tasks
    protected _sorting                  : any
    protected _grouping                 : any
    protected _filters                  : any
    protected _areEmptyFoldersHidden    : boolean = false                               // if true do not display empty folders
    protected _areEmptyGroupsHidden     : boolean = false                               // if true do not display empty groups
    protected _areAddRowsHidden         : boolean = false                               // if true do not display add rows
    protected _highlightItemsMode?      : ItemsHighlightMode
    protected _itemViewBorderPosition!  : CardViewLayout
    protected _colorTasksFieldId!       : ID
    protected _background?              : BackGround
    protected _showSubTasks?            : boolean                                     // true = show subtasks as separate items (cards on Board, rows on List/Table), false = nest subtasks under their parent task

    public titleColumn                  : TitleColumnSettings = {
        minWidth: 280,
        maxWidth: '50%'
    }

    constructor(storedAppSettingsConfig: TasksViewAppSettings | undefined, userLocalSettings: TasksView_LocalSettings, fallbackDefaultSettings: TasksViewAppSettings, options?: TasksView_Settings_Params) {
        debug && console.log(debug, 'constructor', storedAppSettingsConfig, userLocalSettings, fallbackDefaultSettings, options)

        this.projectId                  = options?.projectId

        this.shouldInheritAppSettings   = options?.shouldInheritAppSettings || false

        this._storedAppSettingsConfig   = this.normalizeSettingsConfig(storedAppSettingsConfig) // normalize _storedAppSettingsConfig for comparison and changes detection, storedAppSettingsConfig - settings stored in DB
        this.localSettings              = userLocalSettings                                     // userLocalSettings - initialized local settings instance
        this.defaultAppSettings         = fallbackDefaultSettings                               // fallbackDefaultSettings - default settings values

        // 1. get the actual settings configuration
        this.actualSettings = tasksView_Settings_getActualConfig(this, storedAppSettingsConfig)

        // 2. normalize configuration properties based on version
        this.actualSettings = this.normalizeSettingsConfig(this.actualSettings)

        // 3. initialize settings using the processed config
        this.initProperties(this.actualSettings)
    }

    // leaves only significant settings from passed config
    normalizeSettingsConfig(settingsConfig?: TasksViewAppSettings): TasksViewAppSettings {
        throw Error("Not implemented")
    }

    initProperties(appSettingsConfig: TasksViewAppSettings) {
        this._columns                   = _.get(appSettingsConfig, 'columns',                   this.defaultAppSettings.columns || [])
        this._systemStatusFilter        = _.get(appSettingsConfig, 'systemStatusFilter',        this.defaultAppSettings.systemStatusFilter || SystemStatusGroup.OPEN)
        this._sorting                   = _.get(appSettingsConfig, 'sorting',                   this.defaultAppSettings.sorting)
        this._grouping                  = _.get(appSettingsConfig, 'grouping',                  this.defaultAppSettings.grouping)
        this._filters                   = _.get(appSettingsConfig, 'filters',                   this.defaultAppSettings.filters)
        this._areEmptyFoldersHidden     = _.get(appSettingsConfig, 'areEmptyFoldersHidden',     this.defaultAppSettings.areEmptyFoldersHidden || false)
        this._areEmptyGroupsHidden      = _.get(appSettingsConfig, 'areEmptyGroupsHidden',      this.defaultAppSettings.areEmptyGroupsHidden || true)
        this._areAddRowsHidden          = _.get(appSettingsConfig, 'areAddRowsHidden',          this.defaultAppSettings.areAddRowsHidden || false)
        this._highlightItemsMode        = _.get(appSettingsConfig, 'highlightItemsMode',        this.defaultAppSettings.highlightItemsMode)
        this._itemViewBorderPosition    = _.get(appSettingsConfig, 'itemViewBorderPosition',    this.defaultAppSettings.itemViewBorderPosition || CardViewLayout.COLOR_CODING_NONE)
        this._colorTasksFieldId         = _.get(appSettingsConfig, 'colorTasksFieldId',         this.defaultAppSettings.colorTasksFieldId)
        this._background                = _.get(appSettingsConfig, 'background',                undefined /*this.defaultAppSettings.background*/) // special default value case (see getter)
        this._showSubTasks              = _.get(appSettingsConfig, 'showSubTasks',               this.defaultAppSettings.showSubTasks || false)
    }

    // is used in custom view app
    loadFromJSON(appSettingsConfig: TasksViewAppSettings) {
        this.initProperties(appSettingsConfig)
    }

    // reset all parameters to initial values (delete local settings)
    reset(appSettingsConfig?: TasksViewAppSettings) {
        if (appSettingsConfig) {
            this._storedAppSettingsConfig = appSettingsConfig // reset to this config if passed
        }

        // reset local settings
        this.localSettings.appSettings = null
        this.localSettings.save()

        // reset template
        this._templateId = undefined

        const appSettings = tasksView_Settings_getActualConfig(this, this._storedAppSettingsConfig)
        this.initProperties(appSettings || {})

        gd.bus.triggerEvent(BUS_SETTINGS_MANAGER_CONFIG_CHANGED)
    }

    setInherited() {
        // reset local settings
        this.localSettings.appSettings = null
        this.localSettings.save()

        const appSettings = tasksView_Settings_getActualConfig(this)
        this.initProperties(appSettings || {})

        // reset template
        this.templateId = undefined
    }

    onChangeParameter(paramName: string, value: any) {
        tasksView_Settings_onChangeParameter(this, paramName, value)
    }

    // used by components to detect changes (e.g., AppControlSaveProjectView)
    isConfigChanged(omittedParams: any[] = []) {
        return tasksView_Settings_isConfigChanged(this, omittedParams)
    }

    confirmChanges() {
        this._storedAppSettingsConfig = this.configToJSON()
    }

    get project() {
        return gd.session.projects.get(this.projectId)
    }

    get autoSave() {
        return false
    }

    get mode() {
        return this._mode
    }

    get isInherited() {
        return this._mode === AppSettingsMode.INHERITED
    }

    get isCustom() {
        return this._mode === AppSettingsMode.CUSTOM
    }

    get systemType(): ViewType {
        // return ViewType.PROJECT_TABLE
        throw Error("#3r4235235: systemType Not implemented")
    }

    set templateId(value) {
        this._templateId = value
        tasksView_Settings_onTemplateChanged(this)
    }

    get templateId() {
        return this._templateId
    }

    get inheritedFromProject()  {
        return this._inheritedFromProject
    }

    set columns(value) {
        this.onChangeParameter('columns', value)
    }

    get columns() {
        return this._columns
    }

    set systemStatusFilter(value:SystemStatusGroup) {
        this.onChangeParameter('systemStatusFilter', value)
    }

    get systemStatusFilter() {
        return this._systemStatusFilter
    }

    set sorting(value) {
        this.onChangeParameter('sorting', value)
    }

    get sorting() {
        return this._sorting
    }

    set grouping(value) {
        this.onChangeParameter('grouping', value)
    }

    get grouping() {
        return this._grouping
    }

    set filters(value) {
        this.onChangeParameter('filters', value)
    }

    get filters() {
        return this._filters
    }

    set areEmptyFoldersHidden(value) {
        this.onChangeParameter('areEmptyFoldersHidden', value)
    }

    get areEmptyFoldersHidden() {
        return this._areEmptyFoldersHidden
    }

    set areEmptyGroupsHidden(value) {
        this.onChangeParameter('areEmptyGroupsHidden', value)
    }

    get areEmptyGroupsHidden() {
        return this._areEmptyGroupsHidden
    }

    set areAddRowsHidden(value) {
        this.onChangeParameter('areAddRowsHidden', value)
    }

    get areAddRowsHidden() {
        return this._areAddRowsHidden
    }

    set highlightItemsMode(value) {
        this.onChangeParameter('highlightItemsMode', value)
    }

    get highlightItemsMode() {
        return this._highlightItemsMode
    }

    set itemViewBorderPosition(value) {
        this.onChangeParameter('itemViewBorderPosition', value)
    }

    get itemViewBorderPosition() {
        return this._itemViewBorderPosition
    }

    set colorTasksFieldId(value) {
        this.onChangeParameter('colorTasksFieldId', value)
    }

    get colorTasksFieldId() {
        return this._colorTasksFieldId
    }

    set background(background: BackGround) {
        this.onChangeParameter('background', background)
    }

    get background(): BackGround {
        return this._background || BackGround.WHITE
    }

    set showSubTasks(value: boolean) {
        this.onChangeParameter('showSubTasks', value)
    }

    get showSubTasks(): boolean {
        return !!this._showSubTasks
    }

    set columnsWidth(value) {
        this.localSettings.columnsWidth = value
    }

    get columnsWidth() {
        return this.localSettings.columnsWidth
    }

    set expanded(value) {
        this.localSettings.expanded = value
    }

    get expanded() {
        return this.localSettings.expanded
    }

    // this is manual / local sorting used for specific view only
    // it allows manually sort items in a view that saved in LocalStorage
    // for current user only !
    set sortedItemsIds(value) {
        this.localSettings.sortedItemsIds = value
    }

    get sortedItemsIds() {
        return this.localSettings.sortedItemsIds
    }

    set orderedItemsIds(value) {
        this.localSettings.orderedItemsIds = value
    }

    get orderedItemsIds() {
        return this.localSettings.orderedItemsIds
    }

    set tasksQuickFilterUserId(value: ID | undefined) {
        this.localSettings.tasksQuickFilterUserId = value
    }

    get tasksQuickFilterUserId(): ID | undefined {
        return this.localSettings.tasksQuickFilterUserId
    }

    set showSidePanel(value) {
        this.localSettings.showSidePanel = value
    }

    get showSidePanel() {
        return this.localSettings.showSidePanel
    }

    set breakdown(value) {
        this.localSettings.breakdown = value
        this.localSettings.save()
    }

    get breakdown() {
        return this.localSettings.breakdown
    }

    configToJSON() {
        return this.normalizeConfig2({
            templateId              : this._templateId,

            columns                 : this._columns,
            systemStatusFilter      : this._systemStatusFilter,
            sorting                 : this._sorting,
            grouping                : this._grouping,
            filters                 : this._filters,
            areEmptyFoldersHidden   : this._areEmptyFoldersHidden,
            areEmptyGroupsHidden    : this._areEmptyGroupsHidden,
            areAddRowsHidden        : this._areAddRowsHidden,
            highlightItemsMode      : this._highlightItemsMode,
            itemViewBorderPosition  : this._itemViewBorderPosition,
            colorTasksFieldId       : this._colorTasksFieldId,
            background              : this._background,
            showSubTasks            : this._showSubTasks,
        })
    }

    // is used for saveAppSettings
    configToSave() {
        if (this._inheritedFromProject) {
            return {}
        }

        return this.configToJSON()
    }

    // removes all unnecessary properties and only returns changed config values from defaults
    // to store only what the user has changed compared to the defaultAppSettings
    normalizeConfig2(obj: TasksViewAppSettings): TasksViewAppSettings {

        // if object is empty, just set the default templateId
        if (_.isEmpty(obj)) {
            return {
                templateId      : DEFAULT_TEMPLATE_ID,
                version         : this.version,
            }
        }

        // if templateId is specified, prefer that and skip the rest.
        if (obj.templateId) {
            return {
                templateId      : obj.templateId,
                version         : this.version,
            }
        }

        // config is custom
        const config: TasksViewAppSettings = {}

        if (!_.isNil(obj.columns) && !_.isEqual(obj.columns, this.defaultAppSettings.columns)) {
            config.columns = obj.columns
        }

        if (!_.isNil(obj.systemStatusFilter) && obj.systemStatusFilter !== this.defaultAppSettings.systemStatusFilter) {
            config.systemStatusFilter = obj.systemStatusFilter
        }

        if (!_.isNil(obj.sorting) && obj.sorting !== this.defaultAppSettings.sorting) {
            config.sorting = obj.sorting
        }

        if (!_.isNil(obj.grouping) && !_.isEqual(obj.grouping, this.defaultAppSettings.grouping)) {
            config.grouping = obj.grouping
        }

        if (!_.isNil(obj.filters) && !_.isEqual(obj.filters, this.defaultAppSettings.filters)) {
            config.filters = obj.filters
        }

        if (!_.isNil(obj.areEmptyFoldersHidden) && obj.areEmptyFoldersHidden !== this.defaultAppSettings.areEmptyFoldersHidden) {
            config.areEmptyFoldersHidden = obj.areEmptyFoldersHidden
        }

        if (!_.isNil(obj.areEmptyGroupsHidden) && obj.areEmptyGroupsHidden !== this.defaultAppSettings.areEmptyGroupsHidden) {
            config.areEmptyGroupsHidden = obj.areEmptyGroupsHidden
        }

        if (!_.isNil(obj.areAddRowsHidden) && obj.areAddRowsHidden !== this.defaultAppSettings.areAddRowsHidden) {
            config.areAddRowsHidden = obj.areAddRowsHidden
        }

        if (!_.isNil(obj.highlightItemsMode) && obj.highlightItemsMode !== this.defaultAppSettings.highlightItemsMode) {
            config.highlightItemsMode = obj.highlightItemsMode
        }

        if (!_.isNil(obj.itemViewBorderPosition) && obj.itemViewBorderPosition !== this.defaultAppSettings.itemViewBorderPosition) {
            config.itemViewBorderPosition = obj.itemViewBorderPosition
        }

        if (!_.isNil(obj.colorTasksFieldId) && obj.colorTasksFieldId !== this.defaultAppSettings.colorTasksFieldId) {
            config.colorTasksFieldId = obj.colorTasksFieldId
        }

        if (!_.isNil(obj.background) /*&& obj.background !== this.defaultAppSettings.background*/) {
            config.background = obj.background
        }

        if (
            !_.isNil(obj.showSubTasks)
            && obj.showSubTasks !== this.defaultAppSettings.showSubTasks
        ) {
            config.showSubTasks = obj.showSubTasks
        }

        // add version only if config has any settings otherwise it should remain empty
        if (!_.isEmpty(config)) {
            config.version = this.version
        }

        return config
    }
}